import { Users, Shield, Info } from 'lucide-react';
import { CountBadge } from './UI/CountBadge';
import { TradeBadge } from './UI/TradeBadge';
import {
  assignmentStaffId,
  summarizeAssignedPersonnel,
  groupAssignmentsByDates,
  shouldShowAssignmentGroupMeta,
  formatDualRoleNote,
  isRedundantSafetyTrade,
} from '../utils/assignedPersonnel';

/**
 * Assigned Personnel card for Nightwork / Rest-Day Work request detail.
 *
 * Header badge = unique people on site.
 * Subsections = role coverage (a dual-role person is listed in both).
 * Role is the grouping — do not restamp the other role as a chip on the card.
 */
const AssignedPersonnelSection = ({
  assignments = [],
  request,
  scheduledDateCount = 0,
  theme = 'employee',
}) => {
  const { workers, safety, uniqueCount, dualRolePeople } = summarizeAssignedPersonnel(assignments);
  const workerGroups = groupAssignmentsByDates(workers, request, scheduledDateCount);
  const safetyGroups = groupAssignmentsByDates(safety, request, scheduledDateCount);
  const dualRoleNote = formatDualRoleNote(dualRolePeople);

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-800" />
          Assigned Personnel
          <CountBadge count={uniqueCount} theme={theme} size="md" />
        </h3>
      </div>
      <div className="card-content space-y-4">
        {dualRoleNote && (
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500">{dualRoleNote}</p>
          </div>
        )}

        {workers.length > 0 && (
          <RoleGroup
            label="Workers"
            count={workers.length}
            theme={theme}
            groups={workerGroups}
            variant="worker"
          />
        )}

        {safety.length > 0 && (
          <RoleGroup
            label="Safety Personnel"
            count={safety.length}
            theme={theme}
            groups={safetyGroups}
            variant="safety"
          />
        )}

        {uniqueCount === 0 && (
          <p className="text-xs text-gray-500">No personnel assigned.</p>
        )}
      </div>
    </div>
  );
};

const RoleGroup = ({ label, count, theme, groups, variant }) => {
  const isSafety = variant === 'safety';
  const datePillClass = isSafety
    ? 'bg-green-100 text-green-800'
    : 'bg-blue-100 text-blue-800';
  const showGroupMeta = shouldShowAssignmentGroupMeta(groups);

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <p className="text-xs text-gray-500">{label}</p>
        <CountBadge count={count} theme={theme} size="md" />
      </div>
      <div className="space-y-2">
        {groups.map((g) => (
          <div key={`${variant}-${g.label}`}>
            {showGroupMeta && (
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  g.isCustom ? 'bg-amber-100 text-amber-700' : datePillClass
                }`}>
                  {g.label}
                </span>
                <span className="text-xs text-gray-400">
                  {isSafety
                    ? `${g.assignments.length} personnel`
                    : `${g.assignments.length} worker${g.assignments.length !== 1 ? 's' : ''}`}
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {g.assignments.map((a) => (
                <PersonnelCard
                  key={a.id || `${assignmentStaffId(a)}-${variant}`}
                  assignment={a}
                  isSafety={isSafety}
                  isCustom={g.isCustom}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PersonnelCard = ({ assignment, isSafety, isCustom }) => {
  const name = assignment.employee_name || assignment.employee_staff_id;
  const tradeName = assignment.trade_category_name;
  const showTrade = tradeName && !isRedundantSafetyTrade(tradeName, { inSafetySection: isSafety });
  const cardTone = isSafety
    ? 'bg-green-50'
    : isCustom
      ? 'bg-amber-50'
      : 'bg-gray-50';

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${cardTone}`}>
      {isSafety
        ? <Shield className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
        : <Users className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 font-medium truncate">{name}</p>
        {showTrade && (
          <div className="mt-0.5 flex flex-wrap items-center gap-1">
            <TradeBadge name={tradeName} size="xs" />
          </div>
        )}
      </div>
      <span className="text-xs text-gray-400 font-mono flex-shrink-0">
        {assignment.employee_staff_id}
      </span>
    </div>
  );
};

export default AssignedPersonnelSection;
